import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import MovieListItem from '@/components/base/MovieListItem';
import EmptyState from '@/components/base/EmptyState';
import { categories } from '@/mocks/categories';
import { searchMovies, getHotMovies } from '@/utils/movie';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [input, setInput] = useState(query);
  const [showSuggest, setShowSuggest] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setInput(query);
  }, [query]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setShowSuggest(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const results = useMemo(() => searchMovies(query), [query]);

  const suggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return [];
    return searchMovies(q).slice(0, 6);
  }, [input]);

  const runSearch = (value: string) => {
    const v = value.trim();
    setSearchParams(v ? { q: v } : {});
    setShowSuggest(false);
  };

  const hot = getHotMovies(6);

  usePageMeta({
    title: query ? `搜索“${query}” - 高清电影` : '电影搜索 - 高清电影',
    description: query
      ? `在高清电影中搜索“${query}”，为你找到相关的高清电影作品与影片推荐。`
      : '高清电影搜索页，输入电影名、导演或演员，快速找到想看的高清影片。',
    keywords: `${query ? `${query},` : ''}电影搜索,高清电影,影片搜索`,
  });

  return (
    <PageLayout>
      <div className="w-full px-4 py-8 md:px-8 md:py-10">
        <div className="mx-auto max-w-4xl">
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            搜索电影
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            输入电影名、导演或演员，快速找到你想看的影片。
          </p>

          {/* Search box */}
          <div ref={boxRef} className="relative mt-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                runSearch(input);
              }}
            >
              <div className="flex h-12 items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 focus-within:border-primary-400">
                <i className="ri-search-line text-lg text-foreground-400"></i>
                <input
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    setShowSuggest(true);
                  }}
                  onFocus={() => setShowSuggest(true)}
                  placeholder="例如：星际、爱情、张毅"
                  className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
                />
                <button
                  type="submit"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  搜索
                </button>
              </div>
            </form>

            {/* Suggestions */}
            {showSuggest && suggestions.length > 0 && (
              <div className="absolute inset-x-0 top-full z-30 mt-2 overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-sm">
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setInput(s.title);
                      runSearch(s.title);
                    }}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-background-100 cursor-pointer"
                  >
                    <i className="ri-search-line text-base text-foreground-400"></i>
                    <span className="text-sm text-foreground-800">{s.title}</span>
                    <span className="ml-auto text-xs text-foreground-400">{s.year}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Results */}
          {query ? (
            <div className="mt-8">
              <p className="mb-4 text-sm text-foreground-500">
                关键词「<span className="font-bold text-primary-600">{query}</span>」共找到{' '}
                <span className="font-bold text-foreground-900">{results.length}</span> 条结果
              </p>

              {results.length === 0 ? (
                <div>
                  <EmptyState
                    icon="ri-search-eye-line"
                    title="没有找到相关电影"
                    description="换个关键词试试，或从下面的热门分类中挑选你喜欢的类型。"
                  />
                  <div className="mt-8">
                    <h3 className="mb-4 font-heading text-base font-bold text-foreground-900">
                      热门分类推荐
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((c) => (
                        <Link
                          key={c.key}
                          to={`/category/${c.key}`}
                          className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                        >
                          <i className={`${c.icon} text-base text-primary-500`}></i>
                          {c.name}电影
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {results.map((m) => (
                    <MovieListItem key={m.id} movie={m} highlight={query} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-10">
              <EmptyState
                icon="ri-search-line"
                title="输入关键词开始搜索"
                description="我们还为你准备了一些热门电影，点击下方查看。"
              />
              <div className="mt-8">
                <h3 className="mb-4 font-heading text-base font-bold text-foreground-900">热门电影</h3>
                <div className="flex flex-wrap gap-2">
                  {hot.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => runSearch(m.title)}
                      className="rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600 cursor-pointer"
                    >
                      {m.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}